import { useScript } from '@/composables/useScript';
import { mapToBasicLocale } from '@/utils/i18n';

export default defineNuxtPlugin(async (nuxtApp) => {
  const { oneTrustId, enableOptimize, enableTracify, enableBotfriends } = nuxtApp.$config.public;
  const { locale } = nuxtApp.$i18n;

  const scripts = [];

  // check if we are in a Storybook environment
  const isStorybook =
    typeof window !== 'undefined' &&
    (window.__STORYBOOK_CLIENT_API__ || window.__STORYBOOK_ADDONS_CHANNEL__ || window.parent !== window);

  if (oneTrustId && !isStorybook) {
    try {
      const autoBlock = await fetch(`https://cdn.cookielaw.org/consent/${oneTrustId}/OtAutoBlock.js`);
      const autoBlockContent = await autoBlock.text();

      // inject script inline to save a request
      scripts.push({
        type: 'text/javascript',
        innerHTML: autoBlockContent,
      });
    } catch (e) {
      // if we could not fetch it, that might be because of the browser blocking. So lets add it as a script tag
      scripts.push({
        src: `https://cdn.cookielaw.org/consent/${oneTrustId}/OtAutoBlock.js`,
      });
    }

    scripts.push({
      type: 'text/javascript',
      src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
      'data-document-language': 'true',
      'data-domain-script': oneTrustId,
      defer: true,
    });

    scripts.push({
      type: 'text/javascript',
      innerHTML: `
        window.hideOneTrustBanner = function() {
          // exit if hide style already exists
          if (document.getElementById('onetrust-language-guard')) {
            return;
          }

          const style = document.createElement('style');
          style.innerHTML = '#onetrust-consent-sdk { opacity: 0 !important; }';
          style.id = 'onetrust-language-guard';
          document.head.appendChild(style);
        }

        window.showOneTrustBanner = function() {
          const style = document.getElementById('onetrust-language-guard');
          if (style) {
            style.remove();
          }
        }

        function OptanonWrapper() {
          // Check if language selection is complete
          const hasSelectedLanguage = localStorage.getItem('preferredIso');
          console.log('OneTrust script loaded', hasSelectedLanguage ? 'with language selection' : 'without language selection');

          if (!hasSelectedLanguage) {
            window.hideOneTrustBanner();
          }

          let oldGroups = OnetrustActiveGroups.split(',').filter(g => g);

          window.addEventListener("OneTrustGroupsUpdated", event => {
            const groups = event.detail;

            oldGroups.forEach(group => {
              if (!groups.includes(group)) {
                console.info('[ot] consent revoked for group', group);
                window.location.reload();
              }
            });
          });

          // Dispatch event for other integrations
          window.dispatchEvent(new Event('oneTrustReady'));
        }`,
    });
  }

  if (enableOptimize && !isStorybook) {
    // ABlfyt Script
    useScript(
      {
        src: 'https://cdn.ablyft.com/s/80692412.js',
      },
      'C0004'
    );
  }

  if (enableTracify && !isStorybook) {
    const tracifyIds = {
      de: '16684ff7-c87b-43d3-8fde-4a27478faa75',
      fr: '43eb1cdd-97fb-4187-9156-35b1d7053892',
      nl: 'f90ffc5d-fe3c-426b-bab6-153c3be07286',
      en: 'bc8834f4-8638-4302-bf29-966a528630cd',
      'ch-de': '5fbc5ec2-6c4f-4536-ac8d-47bdf8cc0789',
      'ch-fr': '5fbc5ec2-6c4f-4536-ac8d-47bdf8cc0789',
    };

    useScript(
      {
        innerHTML: `
      (function() {
        var tracifyLoaded = false;

        window.initTracify = function() {
          if (window.Tracify && !tracifyLoaded) {
            tracifyLoaded = true;
            var tracify = window.Tracify({ csid: "${tracifyIds[locale.value]}"});
            tracify.capturePageView();
          }
        };

        // Load Tracify script
        var script = document.createElement('script');
        script.src = 'https://script.blackroll.com/api.v1.js';
        script.async = true;
        script.onload = function() {
          initTracify();
        };
        document.head.appendChild(script);
      })();
      `,
      },
      'C0004'
    );
  }

  if (enableBotfriends && locale.value === 'de' && !isStorybook) {
    useScript({ src: 'https://www.cdn.botfriendsx.com/bfsg/bfsg.min.js' }, 'C0004');
    useScript(
      {
        innerHTML: `!function(s,r,o){var p,a,i,c=[],u=[];function e(){try{var e;if((e="string"==typeof this.response?JSON.parse(this.response):this.response).url){var n=r.getElementsByTagName("script")[0],t=r.createElement("script");
t.async=!0,t.src=e.url,n.parentNode.insertBefore(t,n)}}catch(e){}}s[o]={init:function(){p=arguments;var n={then:function(e){return u.push({type:"t",next:e}),n},catch:function(e){return u.push({type:"c",next:e}),n}};return n},on:function()
{c.push(arguments)},render:function(){a=arguments},destroy:function(){i=arguments}},s.__onWebMessengerHostReady__=function(e){if(delete s.__onWebMessengerHostReady__,s[o]=e,p)for(var n=e.init.apply(e,p),t=0;t<u.length;t++)
{var r=u[t];n="t"===r.type?n.then(r.next):n.catch(r.next)}a&&e.render.apply(e,a),i&&e.destroy.apply(e,i);for(t=0;t<c.length;t++)e.on.apply(e,c[t])};var n=new XMLHttpRequest;n.addEventListener("load",e),
n.open("GET","https://www.cdn.botfriendsx.com/loader.json",!0),n.responseType="json",n.send()}(window,document,"BotfriendsWebchat");

BotfriendsWebchat.init({
  integrationId:"659eb82c2a849284046a3155",
  region: "eu-1",
  locale: "de",
  displayStyle: "button",
  buttonIconUrl: "https://storage.googleapis.com/oneworld-prod/assets/botfriends-chatbot/br-chatbot-icon.png?v=1753776531",
  businessIconUrl: "https://storage.googleapis.com/oneworld-prod/assets/botfriends-chatbot/br-chatbot-icon.png?v=1753776531",
  customText: {
    inputPlaceholder: "Schreiben Sie uns eine Nachricht.",
    introductionText: "Unsere Datenschutzbestimmungen findest Du hier <a href='https://blackroll.com/de/service/datenschutz' target='_blank'>Datenschutzerklärung</a>.",
    messageRelativeTimeJustNow: "Gerade eben",
    messageRelativeTimeDay: "vor {value} Tagen",
    messageRelativeTimeHour: "vor {value} Stunden",
    messageRelativeTimeMinute: "vor {value} Minuten",
    messageRelativeTimeYesterday: "Gestern",
    messageTimestampFormat: "HH:mm",
    messageTimestampHeaderFormat: "DD.MM.YY",
    messageSending: "Senden...",
    messageDelivered: "Zugestellt",
    headerText: "Wir sind gerne für Dich da!",
    sendButtonText: 'Senden'
  },
  menuItems: {
    imageUpload: false,
    fileUpload: false,
    shareLocation: false
  },
  delegate: getDelegates()
})
.then(function() {
  BotfriendsWebchatBFSG.init(BotfriendsWebchat)
})
.then(function () {
  const webMessenger = document.getElementById('web-messenger-container')
  const messageContainer = webMessenger.contentWindow.document.getElementsByClassName('messages-container')[0]
  const poweredByBOTfriendsDiv = document.createElement('div')
  const poweredByBOTfriendsDivStyle = 'padding-bottom: 10px'
  poweredByBOTfriendsDiv.classList.add('logo')
  poweredByBOTfriendsDiv.style.cssText = poweredByBOTfriendsDivStyle
  const poweredByBOTfriendsImage = document.createElement('img')
  poweredByBOTfriendsImage.src = 'https://www.cdn.botfriendsx.com/botfriends_logo.png'
  poweredByBOTfriendsImage.style.cssText = 'display: block; margin-left: auto; margin-right: auto; width: 164px; height: 20px'
  poweredByBOTfriendsDiv.appendChild(poweredByBOTfriendsImage)
  messageContainer.appendChild(poweredByBOTfriendsDiv)
})
function getDelegates () {
  return {
    beforeDisplay(message, data) {
      if (message.metadata && message.metadata.isHidden) {
        return null
      }
      var isAgentMessage = message.metadata && message.metadata.isAgentMessage
      if (!isAgentMessage) {
        message.avatarUrl = 'https://storage.googleapis.com/media-im/1729756474222Bildschirmfoto%202024-10-22%20um%2014.07.39.png'
      }
      return message
    },
    beforeSend(message, data) {
      if (!message.metadata) {
        message.metadata = {}
      }
      var urlParams = new URLSearchParams(window.location.search);
      var kundennummer = urlParams.get('kundennummer')
      if (!kundennummer) {
        kundennummer = 'Unknown'
      }
      message.metadata.kundennummer = kundennummer
      if (message.text && message.text.startsWith('conversation:start:')) {
        message.metadata.isHidden = true
      }
      return message
    }
  }
}
BotfriendsWebchat.on('widget:opened', function () {
  var conversation = BotfriendsWebchat.getConversation()
  if (conversation && conversation.messages && !conversation.messages.length) {
    BotfriendsWebchat.sendMessage('conversation:start:en')
  }
})`,
      },
      'C0004'
    );
  }

  // Yotpo locale-aware date formatter used by loyalty widgets
  const yotpoDateLocale = nuxtApp.$getCurrentLocaleConfig()?.language || 'de-DE';

  scripts.push({
    type: 'text/javascript',
    innerHTML: `
      (function() {
        var fmt = new Intl.DateTimeFormat('${yotpoDateLocale}', { day: '2-digit', month: '2-digit', year: 'numeric' });
        window.yotpoDateFormatter = function(dateStr) {
          if (dateStr === 'Never') return '';
          var ts = Date.parse(dateStr);
          if (isNaN(ts)) return dateStr;
          return fmt.format(new Date(ts));
        };
      })();
    `,
  });

  const yotpoLoaderKey = nuxtApp.$config.public.yotpoLoaderKey;
  if (yotpoLoaderKey) {
    scripts.push({
      type: 'text/javascript',
      src: `https://cdn-widgetsrepository.yotpo.com/v1/loader/${encodeURIComponent(yotpoLoaderKey)}`,
      defer: true,
    });
  }

  const localeConfig = nuxtApp.$getCurrentLocaleConfig() || {};
  const yotpoReviewsLoaderKey = localeConfig.yotpoReviewsLoaderKey;
  const languageCode = mapToBasicLocale(locale.value);

  if (yotpoReviewsLoaderKey) {
    const reviewsLoaderUrl =
      `https://cdn-widgetsrepository.yotpo.com/v1/loader/${encodeURIComponent(yotpoReviewsLoaderKey)}` +
      (languageCode ? `?languageCode=${encodeURIComponent(languageCode)}` : '');

    scripts.push({
      type: 'text/javascript',
      src: reviewsLoaderUrl,
      async: true,
    });
  }

  useHead({
    script: scripts,
  });
});
